import React, { useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { authAPI } from '../../services/api';
import { useTranslation } from '../../hooks/useTranslation';

type Props = {
  onSuccess?: () => void;
  onError?: (message: string) => void;
};

// Lightweight Google Identity Services (Web) integration without extra deps
export default function GoogleSignInWeb({ onSuccess, onError }: Props) {
  const divRef = useRef<HTMLDivElement | null>(null);
  const { language } = useTranslation();

  useEffect(() => {
    if (Platform.OS !== 'web') return; // Web-only component

    const clientId = process.env.EXPO_PUBLIC_GOOGLE_OAUTH_CLIENT_ID;
    if (!clientId) {
      onError?.('Google OAuth client ID is not configured');
      return;
    }

    // Inject Google Identity Services script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      try {
        // @ts-ignore
        const google = window.google;
        if (!google?.accounts?.id) {
          onError?.('Google Identity Services unavailable');
          return;
        }

        google.accounts.id.initialize({
          client_id: clientId,
          callback: async (response: any) => {
            try {
              const credential: string | undefined = response?.credential;
              if (!credential) {
                onError?.('No credential returned by Google');
                return;
              }
              await authAPI.loginWithGoogleIdToken(credential);
              onSuccess?.();
            } catch (e: any) {
              const msg = e?.response?.data?.detail || 'Google sign-in failed';
              onError?.(msg);
            }
          },
          auto_select: false,
          cancel_on_tap_outside: true,
        });

        if (divRef.current) {
          google.accounts.id.renderButton(divRef.current, {
            theme: 'outline',
            size: 'large',
            shape: 'rectangular',
            text: 'signin_with',
            logo_alignment: 'left',
            width: 320,
            // Follow the app language, not the browser locale, so the button
            // doesn't render in a third language next to localized UI.
            locale: language,
          });
        }
      } catch {
        onError?.('Failed to initialize Google sign-in');
      }
    };
    script.onerror = () => onError?.('Failed to load Google script');
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [onError, onSuccess, language]);

  if (Platform.OS !== 'web') return null;
  return <div ref={divRef} />;
}
